import { ReadOnlyItem, Row, Section, Text } from '@ydistri/ds';
import React, { useCallback, useMemo } from 'react';
import { BrandResponse, ProductResponse } from '@ydistri/api-sdk';
import CalculationRedistributionSectionHeader from './CalculationRedistributionSectionHeader';
import { useGetCalculationSkusQuery } from '../../../../../Calculations/apiCalculationsSkus';
import { extractCategoryTitles, trimArray } from '../../../redistributionLib';
import { Divider, Popover, Space, Spin } from 'antd';
import { generatePath } from 'react-router';
import { ROUTES } from '../../../../../../components/menu/getMenuLeftItems';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../../store';
import { useStableParams } from '../../../../../../routes/useStableParams';
import EditRedistributionButton from '../../../../Editation/EditRedistributionButton';

interface CategoryListProps {
  categories: string[];
  maxCategories?: number;
}

/**
 * Display a list of categories separated by a vertical divider. If the list is longer than the maxCategories prop, the
 * list will be trimmed and a tooltip will be displayed with the full list.
 * @param categories
 * @param maxCategories Display only given number of categories. Component will display the first and last maxCategories - 1
 * @constructor
 */
const CategoryList: React.FC<CategoryListProps> = ({ categories, maxCategories = 4 }) => {
  const trimCategories = categories.length > maxCategories;

  const body = useCallback((items: string[]) => {
    return (
      <Space split={splitter}>
        {items.map((categoryName, i) => (
          // eslint-disable-next-line react/no-array-index-key -- category name is not unique
          <Text key={`${categoryName}-${i}`}>{categoryName}</Text>
        ))}
      </Space>
    );
  }, []);

  if (trimCategories) {
    const tmpCategoryNames = trimArray(categories, maxCategories, '...');
    const tooltipContent = categories.join(' / ');
    return (
      <Popover content={tooltipContent} placement="bottomLeft" trigger="hover">
        {body(tmpCategoryNames)}
      </Popover>
    );
  } else {
    return body(categories);
  }
};

interface CalculationRedistributionProductSummaryProps {
  product: ProductResponse;
  skuId: number;
  calculationId: number;
  brand?: BrandResponse;
  description?: string | null;
}

const splitter = <Divider type="vertical" />;

const CalculationRedistributionProductSummary: React.FC<
  CalculationRedistributionProductSummaryProps
> = ({ product, skuId, calculationId, brand, description }) => {
  const { projectShortName } = useStableParams();
  const selectedCategorySlug = useSelector(
    (state: ReduxState) => state.categoryTreeReducer.currentlySelectedCategorySlug,
  );

  const { data: skuConfiguration, isFetching } = useGetCalculationSkusQuery({
    skuId: skuId,
    calculationId: calculationId,
  });

  const categoryNames = useMemo(() => {
    if (skuConfiguration) {
      return extractCategoryTitles(skuConfiguration.categories);
    } else {
      return [];
    }
  }, [skuConfiguration]);

  const brandComponent = useMemo(() => {
    const tmpBrand = brand ?? product.brand;
    const brandName = tmpBrand?.name;

    if (brandName) {
      return <ReadOnlyItem label="Brand" value={brandName} />;
    }
  }, [brand, product.brand]);

  const categoryComponent = useMemo(() => {
    return (
      <ReadOnlyItem label="Category">
        {isFetching ? (
          <Spin size="small" />
        ) : (
          <CategoryList categories={categoryNames} maxCategories={3} />
        )}
      </ReadOnlyItem>
    );
  }, [categoryNames, isFetching]);

  const header = useMemo(() => {
    const urlPath = generatePath(ROUTES.detail, {
      projectShortName: projectShortName ?? '',
      slug: selectedCategorySlug,
    });

    const productDetailUrl = `${urlPath}?product=${product.id}`;

    return (
      <Row $gap="1rem" $alignItems="center" $flexGrow={1} $justifyContent="space-between">
        <Row $gap="1rem" $alignItems="center">
          <Space split={splitter}>
            <CalculationRedistributionSectionHeader
              type="product"
              entity={product}
              link={productDetailUrl}
            />
            {brandComponent}
            {categoryComponent}
          </Space>
        </Row>
        <EditRedistributionButton calculationId={calculationId} productId={product.id} />
      </Row>
    );
  }, [
    brandComponent,
    calculationId,
    categoryComponent,
    product,
    projectShortName,
    selectedCategorySlug,
  ]);

  return (
    <Section header={header}>
      {description && <ReadOnlyItem label="Picking instruction">{description}</ReadOnlyItem>}
    </Section>
  );
};

const MemoCalculationRedistributionProductSummary = React.memo(
  CalculationRedistributionProductSummary,
);
MemoCalculationRedistributionProductSummary.displayName = 'CalculationRedistributionProductSummary';

export default MemoCalculationRedistributionProductSummary;
